<script setup>
import { getCurrentInstance } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { useLoading } from '@/composables/useLoading'

const { proxy } = getCurrentInstance()
const $t = proxy.$t
const { showLoading, hideLoading } = useLoading()

const props = defineProps({
    user: Object,
})

const form = useForm({
    _method: 'PUT',
    first_name: props.user?.first_name || '',
    last_name: props.user?.last_name || '',
    name: props.user?.name || '',
    birthday: props.user?.birthday || '',
    email: props.user?.email || '',
    photo: null,
    invoice_company_name: props.user?.invoice_company_name || '',
    invoice_vat_number: props.user?.invoice_vat_number || '',
    invoice_paypal: props.user?.invoice_paypal || '',
    invoice_address_country: props.user?.invoice_address_country || '',
    invoice_address_state: props.user?.invoice_address_state || '',
    invoice_address_street_name: props.user?.invoice_address_street_name || '',
    invoice_address_street_number: props.user?.invoice_address_street_number || '',
    invoice_address_number: props.user?.invoice_address_number || '',
    invoice_address_zipcode: props.user?.invoice_address_zipcode || '',
})

const updateProfileInformation = () => {
    showLoading($t('Updating invoice information...'));
    const routeName = proxy.route ? proxy.route('client.profile-information.update') : '/profile-information'
    form.post(routeName, {
        errorBag: 'updateProfileInformation',
        preserveScroll: true,
        onFinish: () => hideLoading(),
    })
}
</script>

<template>
    <form @submit.prevent="updateProfileInformation" class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <!-- Company Name -->
            <div class="field">
                <label for="invoice_company_name" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Company name') }}
                </label>
                <InputText
                    id="invoice_company_name"
                    v-model="form.invoice_company_name"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.invoice_company_name }"
                    autocomplete="organization"
                />
                <small v-if="form.errors.invoice_company_name" class="p-error">
                    {{ form.errors.invoice_company_name }}
                </small>
            </div>

            <!-- VAT Number -->
            <div class="field">
                <label for="invoice_vat_number" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('VAT number') }}
                </label>
                <InputText
                    id="invoice_vat_number"
                    v-model="form.invoice_vat_number"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.invoice_vat_number }"
                />
                <small v-if="form.errors.invoice_vat_number" class="p-error">
                    {{ form.errors.invoice_vat_number }}
                </small>
            </div>

            <!-- PayPal -->
            <div class="field">
                <label for="invoice_paypal" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('PayPal') }}
                </label>
                <InputText
                    id="invoice_paypal"
                    v-model="form.invoice_paypal"
                    type="email"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.invoice_paypal }"
                    autocomplete="email"
                    placeholder="paypal@example.com"
                />
                <small v-if="form.errors.invoice_paypal" class="p-error">
                    {{ form.errors.invoice_paypal }}
                </small>
            </div>
        </div>

        <Divider />

        <h4 class="text-lg font-semibold text-surface-900">{{ $t('Invoice Address') }}</h4>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Country -->
            <div class="field">
                <label for="invoice_address_country" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Country') }}
                </label>
                <InputText
                    id="invoice_address_country"
                    v-model="form.invoice_address_country"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.invoice_address_country }"
                    autocomplete="country"
                />
                <small v-if="form.errors.invoice_address_country" class="p-error">
                    {{ form.errors.invoice_address_country }}
                </small>
            </div>

            <!-- State -->
            <div class="field">
                <label for="invoice_address_state" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('State') }}
                </label>
                <InputText
                    id="invoice_address_state"
                    v-model="form.invoice_address_state"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.invoice_address_state }"
                    autocomplete="address-level1"
                />
                <small v-if="form.errors.invoice_address_state" class="p-error">
                    {{ form.errors.invoice_address_state }}
                </small>
            </div>

            <!-- Street Name -->
            <div class="field">
                <label for="invoice_address_street_name" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Street name') }}
                </label>
                <InputText
                    id="invoice_address_street_name"
                    v-model="form.invoice_address_street_name"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.invoice_address_street_name }"
                    autocomplete="address-line1"
                />
                <small v-if="form.errors.invoice_address_street_name" class="p-error">
                    {{ form.errors.invoice_address_street_name }}
                </small>
            </div>

            <!-- Street Number -->
            <div class="field">
                <label for="invoice_address_street_number" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Number') }}
                </label>
                <InputText
                    id="invoice_address_street_number"
                    v-model="form.invoice_address_street_number"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.invoice_address_street_number }"
                />
                <small v-if="form.errors.invoice_address_street_number" class="p-error">
                    {{ form.errors.invoice_address_street_number }}
                </small>
            </div>

            <!-- Apartment/Suite -->
            <div class="field">
                <label for="invoice_address_number" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Apartment, suit, etc') }}
                </label>
                <InputText
                    id="invoice_address_number"
                    v-model="form.invoice_address_number"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.invoice_address_number }"
                    autocomplete="address-line2"
                />
                <small v-if="form.errors.invoice_address_number" class="p-error">
                    {{ form.errors.invoice_address_number }}
                </small>
            </div>

            <!-- Zip Code -->
            <div class="field">
                <label for="invoice_address_zipcode" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Zip code') }}
                </label>
                <InputText
                    id="invoice_address_zipcode"
                    v-model="form.invoice_address_zipcode"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.invoice_address_zipcode }"
                    autocomplete="postal-code"
                />
                <small v-if="form.errors.invoice_address_zipcode" class="p-error">
                    {{ form.errors.invoice_address_zipcode }}
                </small>
            </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-4 pt-4">
            <Button
                type="submit"
                :label="form.processing ? $t('Saving...') : $t('Save')"
                icon="pi pi-save"
                :loading="form.processing"
                :disabled="form.processing"
            />

            <div v-if="form.recentlySuccessful" class="flex items-center gap-2 text-green-600">
                <i class="pi pi-check-circle"></i>
                <span class="text-sm font-medium">{{ $t('Saved.') }}</span>
            </div>
        </div>
    </form>
</template>

<style scoped>
.field {
    @apply flex flex-col;
}

.p-error {
    @apply text-red-500 text-xs mt-1;
}

.p-invalid {
    @apply border-red-500;
}
</style>